// ============================================================
// auditSentence.tsx
// Shared sentence-renderer for audit-log entries. Consumed by
// BOTH the Admin viewer (AdminPage.tsx → AuditLogPanel) and the
// per-case timeline (sections/CaseHistorySection.tsx). Single
// source of truth so a new event-type vocabulary lands in one
// place and benefits both surfaces.
//
// Renders each entry as React.ReactNode:
//   - Subject:     <strong>{actorEmail}</strong>
//   - Variables:   <em>...</em>
//   - ISO dates:   formatReadableDate(...) → "11 Jun 2026"
//   - Unknown:     safe generic fallback so the viewer never
//                  breaks on a new action vocabulary before the
//                  frontend learns about it.
//
// All sentence detail keys reflect the LIVE action payloads
// (per Reuben's taskController / caseController / deadline
// controllers). The narrowing reads through String(...) so a
// missing key shows as "undefined" rather than crashing.
// ============================================================
import { historyDate } from './billHistory'
import React from 'react'
import type { AuditLogEntry } from '../types/auditLog.types'
import { useCasevoStore } from '../store/useCasevoStore'
import { abuseTypeLabel, claimRouteLabel, claimTrackLabel, claimTypeLabel, complexityBandLabel, holidayRegimeLabel } from './claimTypes'

// Small ISO-date formatter used by the case_deadline.* and
// case.closed/reopened templates. en-GB short-month format
// ("11 Jun 2026") reads natural for the UK-solicitor audience.
// Guards against non-strings and invalid dates by returning the
// input verbatim — the sentence still reads, just less polished.
export const formatReadableDate = (iso: unknown): string => {
  if (typeof iso !== 'string' || iso.length === 0) return String(iso)
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

// The actor shown in History: "First Last" from the firm's members, else the
// email stored on the audit row (a former member, or no name set).
export const actorLabel = (entry: Pick<AuditLogEntry, 'actorUserId' | 'actorEmail'>): string => {
  const u = useCasevoStore.getState().firmUsers.find((x) => x.id === entry.actorUserId)
  const name = u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : ''
  return name || entry.actorEmail
}

// (B3b) A field key with no label in a map below → readable words, never the
// code key: "clientHasEmailAddress" → "client has email address", "sraNumber"
// → "SRA number". Anything that isn't a plain identifier → "a detail".
const AUDIT_ACRONYMS = new Set(['sra', 'vat', 'ni', 'url', 'ate', 'tpi', 'gp', 'cfa', 'dob', 'pdf', 'id', 'llp'])
export const auditFieldLabel = (key: unknown, labels: Record<string, string> = {}, capitalise = false): string => {
  const k = typeof key === 'string' ? key.trim() : ''
  if (k && Object.prototype.hasOwnProperty.call(labels, k)) return labels[k]
  if (!/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(k)) return 'a detail'
  const words = k.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').toLowerCase().split(/\s+/).filter(Boolean)
  const text = words.map((w) => (AUDIT_ACRONYMS.has(w) ? w.toUpperCase() : w)).join(' ')
  return capitalise ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

// ── Client edits (Big-audit batch 4, M9): field names + masked values ──
const CLIENT_FIELD_LABELS: Record<string, string> = {
  niNumber: 'NI number', dateOfBirth: 'date of birth', notes: 'notes', emailAddress: 'email', hasEmailAddress: 'has an email address',
  mainContactNumber: 'main contact number', mobileNumber: 'mobile', forename: 'forename', surname: 'surname', title: 'title',
}
const clientFieldLabel = (f: string): string => CLIENT_FIELD_LABELS[f] ?? f.replace(/([A-Z])/g, ' $1').toLowerCase()
const clientChangeText = (c: { from?: unknown; to?: unknown } | undefined): string => {
  const shown = (v: unknown): string => (v === null || v === undefined || v === '' ? '(empty)' : String(v))
  if (typeof c?.to === 'string' && /^changed \(/.test(c.to)) return c.to
  if (typeof c?.from === 'string' && /^changed \(/.test(c.from)) return `${c.from}, now ${shown(c.to)}`
  return `${shown(c?.from)} → ${shown(c?.to)}`
}

// ── Claim fields (B2: case.claim_updated details.changes) ──
// Readable names + values, e.g. "Claim type" "Road traffic (RTA)".
export const CLAIM_FIELD_LABELS: Record<string, string> = {
  claimType: 'Claim type',
  claimRoute: 'Route',
  track: 'Track',
  complexityBand: 'Complexity band',
  frcRegime: 'Fixed recoverable costs',
  dateOfKnowledge: 'Date of knowledge',
  dateOfDeath: 'Date of death',
  // B2b
  holidayRegime: 'Type of holiday claim',
  travelArrivalDate: 'Arrival date',
  foreignLimitationMonths: 'Foreign limitation period',
  abuseType: 'Type of abuse',
  policeFirstReportedAt: 'First reported to police',
  overseasOperation: 'Overseas operation',
  firstSymptomsDate: 'First symptoms',
  diagnosisDate: 'Diagnosis',
}
export const claimValueText = (field: string, v: unknown): string => {
  if (v === null || v === undefined || v === '') return 'not set'
  switch (field) {
    case 'claimType': return claimTypeLabel(v) ?? String(v)
    case 'claimRoute': return claimRouteLabel(v) ?? String(v)
    case 'track': return claimTrackLabel(v) ?? String(v)
    case 'complexityBand': return typeof v === 'number' ? complexityBandLabel(v) : String(v)
    case 'frcRegime':
    case 'overseasOperation': return v === true ? 'Yes' : v === false ? 'No' : String(v)
    case 'holidayRegime': return holidayRegimeLabel(v) ?? String(v)
    case 'abuseType': return abuseTypeLabel(v) ?? String(v)
    case 'foreignLimitationMonths': return typeof v === 'number' ? `${v} month${v === 1 ? '' : 's'}` : String(v)
    case 'dateOfKnowledge':
    case 'dateOfDeath':
    case 'travelArrivalDate':
    case 'policeFirstReportedAt':
    case 'firstSymptomsDate':
    case 'diagnosisDate': return formatReadableDate(v)
    default: return String(v)
  }
}
// One change as a phrase: "set Track to Fast track" / "changed Claim type from
// Road traffic (RTA) to Fatal" / "cleared Date of death (was 3 Mar 2024)".
export const claimChangePhrase = (c: { field?: unknown; from?: unknown; to?: unknown }): React.ReactNode => {
  const field = String(c.field)
  const name = auditFieldLabel(field, CLAIM_FIELD_LABELS, true)
  const empty = (v: unknown): boolean => v === null || v === undefined || v === ''
  if (empty(c.from)) return <>{'set '}{name}{' to '}<em>{claimValueText(field, c.to)}</em></>
  if (empty(c.to)) return <>{'cleared '}{name}{' (was '}<em>{claimValueText(field, c.from)}</em>{')'}</>
  return <>{'changed '}{name}{' from '}<em>{claimValueText(field, c.from)}</em>{' to '}<em>{claimValueText(field, c.to)}</em></>
}

export const renderActionSentence = (entry: AuditLogEntry): React.ReactNode => {
  const { action, details } = entry
  // Who did it: the person's NAME when they have one (live from the firm's
  // members), else the email recorded with the row.
  const actorEmail = actorLabel(entry)

  // ── Invoicing Stage 1 — bills + disbursements ──────────
  if (action.startsWith('invoice.') || action.startsWith('disbursement.')) {
    const pounds = (p: unknown) => typeof p === 'number' ? `£${(p / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''
    const num = typeof details.number === 'string' ? details.number : ''
    const desc = typeof details.description === 'string' ? `“${details.description}”` : ''
    const text: Record<string, string> = {
      'invoice.drafted': `created a draft bill${details.totalPence != null ? ` (${pounds(details.totalPence)})` : ''}`,
      'invoice.updated': 'changed a draft bill',
      'invoice.line_added': `added ${desc || 'a line'} to a draft bill`,
      'invoice.line_updated': `changed ${desc || 'a line'} on a draft bill`,
      'invoice.line_removed': `removed ${desc || 'a line'} from a draft bill`,
      'invoice.billed_lines_removed': `removed ${typeof details.count === 'number' && details.count !== 1 ? `${details.count} lines` : 'a line'} that can’t be billed (already billed elsewhere, or no longer billable) from a draft bill`,
      'invoice.line_refreshed': `updated ${desc || 'a line'} on a draft bill to its time entry’s current figures`,
      'invoice.line_synced': `brought ${desc || 'a line'} on a draft bill up to date with its edited time entry`,
      'invoice.submitted': 'sent a bill for approval',
      'invoice.approved': 'approved a bill',
      'invoice.reopened': 'reopened a bill for changes',
      'invoice.issued': `issued bill ${num}${details.totalPence != null ? ` for ${pounds(details.totalPence)}` : ''}${typeof details.signatory === 'string' && details.signatory ? `, signed by ${details.signatory}` : ''}`,
      // B3 — a non-email delivery.
      'invoice.delivered': `marked ${num || 'a bill'} as ${details.method === 'HAND' ? 'handed over' : 'sent by post'}${historyDate(details.date) ? ` on ${historyDate(details.date)}` : ''}`,
      'invoice.sent': `emailed ${num || 'a bill'}${Array.isArray(details.to) ? ` to ${(details.to as string[]).join(', ')}` : ''}${historyDate(entry.createdAt) ? ` on ${historyDate(entry.createdAt)}` : ''}`,
      'invoice.sent_back': `sent a bill back for changes${typeof details.reason === 'string' && details.reason ? ` (“${details.reason}”)` : ''}`,
      'invoice.payment_reversed': `reversed a payment${details.amountPence != null ? ` of ${pounds(details.amountPence)}` : ''}${num ? ` on ${num}` : ''}`,
      'invoice.written_off': `wrote off ${details.amountPence != null ? pounds(details.amountPence) : 'the balance'}${num ? ` on ${num}` : ''}`,
      'invoice.voided': `voided ${num ? `request ${num}` : 'a request for payment on account'}`,
      'invoice.pdf_restricted_copy': `opened a restricted copy of ${num || 'a bill'}’s PDF`,
      'invoice.credit_note_drafted': `drafted a credit note${details.amountPence != null ? ` for ${pounds(details.amountPence)}` : ''}`,
      'invoice.credit_note_issued': `issued credit note ${num}${details.billNumber ? ` against ${String(details.billNumber)}` : ''}${details.amountPence != null ? ` (${pounds(details.amountPence)})` : ''}`,
      'invoice.credit_note_deleted': 'deleted a draft credit note',
      'invoice.payment_recorded': `recorded a payment of ${pounds(details.amountPence)}${num ? ` on ${num}` : ''}`,
      // B3b — an issued credit note sent / delivered (details.number = the credit note's).
      'invoice.credit_note_sent': `emailed ${num ? `credit note ${num}` : 'a credit note'}${Array.isArray(details.to) ? ` to ${(details.to as string[]).join(', ')}` : ''}${historyDate(entry.createdAt) ? ` on ${historyDate(entry.createdAt)}` : ''}`,
      'invoice.credit_note_delivered': `marked ${num ? `credit note ${num}` : 'a credit note'} as ${details.method === 'HAND' ? 'handed over' : 'sent by post'}${historyDate(details.date) ? ` on ${historyDate(details.date)}` : ''}`,
      'invoice.deleted': 'deleted a draft bill',
      'disbursement.created': `recorded disbursement ${desc}${details.amountPence != null ? ` (${pounds(details.amountPence)})` : ''}`,
      'disbursement.updated': 'updated a disbursement',
      'disbursement.deleted': `deleted disbursement ${desc}`,
    }
    if (text[action]) return (<><strong className="font-medium">{actorEmail}</strong>{` ${text[action]}`}</>)
  }

  // ── Stage 4 — settlement (CFA, figures, interims, recoveries) ──
  // Never shows £ in the sentence: the server withholds the figures from
  // viewers without "See colleagues' billing" (details.fields = names only).
  if (action.startsWith('pi_matter.') || action.startsWith('pi_interim.') || action.startsWith('cfa.') || action.startsWith('pi_disbursement.')) {
    const fields = Array.isArray(details.fields) ? (details.fields as string[])
      : details.changes && typeof details.changes === 'object' ? Object.keys(details.changes as object) : []
    const desc = typeof details.description === 'string' ? ` “${details.description}”` : ''
    const n = fields.length
    const text: Record<string, string> = {
      'pi_matter.figures_changed': `changed the settlement figures${n ? ` (${n} field${n === 1 ? '' : 's'})` : ''}`,
      'pi_matter.base_costs_overridden': 'overrode the base costs for the settlement',
      'pi_matter.base_costs_override_cleared': 'went back to the recorded time for the base costs',
      'pi_matter.adult_confirmed': 'confirmed the client is an adult',
      'pi_interim.created': 'recorded an interim payment',
      'pi_interim.updated': 'changed an interim payment',
      'pi_interim.deleted': 'removed an interim payment',
      'cfa.created': 'created the CFA from the firm defaults',
      'cfa.terms_changed': 'changed the CFA terms',
      'cfa.reviewed': 'marked the migrated CFA reviewed',
      'pi_disbursement.recovery_changed': `updated the recovery of disbursement${desc}`,
    }
    if (text[action]) return (<><strong className="font-medium">{actorEmail}</strong>{` ${text[action]}`}</>)
  }

  // ── Admin actions (firm-scoped, no caseId) ──────────────
  if (action === 'firm.two_person_checks_updated') {
    const word = (v: unknown): string => (v === false ? 'off' : 'on')
    return (<><strong className="font-medium">{actorEmail}</strong>{` turned two-person checks ${word(details.to)} (they were ${word(details.from)})`}</>)
  }
  if (action === 'settlement.statement_checked' && details.selfBasis === 'CHECKS_OFF') {
    return (<><strong className="font-medium">{actorEmail}</strong>{' approved a settlement statement. Approved by the same person who prepared it (two-person checks are off at this firm)'}</>)
  }
  if (action === 'firm.invoice_signature_updated' || action === 'firm.invoice_signature_removed') {
    return (<><strong className="font-medium">{actorEmail}</strong>{action === 'firm.invoice_signature_updated' ? ' uploaded a new invoice signature image' : ' removed the invoice signature image'}</>)
  }
  // B3b — per-user signature images, and the one-time move of the firm's.
  if (action === 'user.signature_updated' || action === 'user.signature_removed') {
    const whose = details.byAdmin ? 'a team member’s' : 'their'
    return (<><strong className="font-medium">{actorEmail}</strong>{action === 'user.signature_updated' ? ` uploaded ${whose} bill signature image` : ` removed ${whose} bill signature image`}</>)
  }
  if (action === 'firm.invoice_signature_moved') {
    return (<><strong className="font-medium">{actorEmail}</strong>{' moved the firm’s signature image to '}<em>{typeof details.toUser === 'string' && details.toUser ? details.toUser : 'a team member'}</em></>)
  }
  if (action === 'firm.billing_settings_updated') {
    // Invoicing Stage 0 — details.changed carries the field names only here.
    const fields = details.changed && typeof details.changed === 'object' ? Object.keys(details.changed as object) : []
    const LABELS: Record<string, string> = {
      vatNumber: 'VAT number', sraNumber: 'SRA number', invoiceNumberFormat: 'bill numbering',
      billApprovalRequired: 'bill approval', defaultSuccessPercent: 'default success fee',
      billingAddress: 'letterhead address', billingPhone: 'phone', billingEmail: 'billing email', bankAccountName: 'bank account name',
      bankSortCode: 'sort code', bankAccountNumber: 'account number', paymentTermsDays: 'payment terms', complaintsContact: 'complaints contact',
      invoiceSignatoryName: 'signatory name', invoiceSignatoryTitle: 'signatory title',
    }
    const what = fields.map((f) => auditFieldLabel(f, LABELS)).join(', ')
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {what ? ` updated the firm’s billing settings (${what})` : ' updated the firm’s billing settings'}
      </>
    )
  }
  // ── B3 — bill wording, bill email consent, job title ──
  if (action === 'firm.bill_wording_updated' || action === 'firm.bill_wording_approved' || action === 'firm.bill_wording_unapproved') {
    const WORDING_LABELS: Record<string, string> = {
      legalName: 'legal name', companyRegistration: 'company registration', complaintsContactName: 'complaints contact name',
      complaintsContactRole: 'complaints contact role', complaintsEmail: 'complaints email', complaintsPhone: 'complaints phone',
      complaintsProcedureUrl: 'complaints procedure link', billRightsWording: 'rights paragraph', billComplaintsWording: 'complaints wording',
      billSraFooter: 'SRA footer', vatNumber: 'VAT number', sraNumber: 'SRA number', complaintsContact: 'complaints contact',
      legalStructure: 'legal structure',
    }
    const list = (v: unknown): string => (Array.isArray(v) ? (v as string[]) : v && typeof v === 'object' ? Object.keys(v as object) : []).map((f) => auditFieldLabel(f, WORDING_LABELS)).join(', ')
    const text = action === 'firm.bill_wording_approved'
      ? ' approved the bill wording'
      : action === 'firm.bill_wording_updated'
        ? ` changed the bill wording${list(details.changed) ? ` (${list(details.changed)})` : ''}${details.approvalReset ? '. Its approval was withdrawn' : ''}`
        : details.automatic
          ? ` changed ${list(details.fields) || 'the firm details'}, so the bill wording went back to draft`
          : ' withdrew the approval of the bill wording'
    return (<><strong className="font-medium">{actorEmail}</strong>{text}</>)
  }
  if (action === 'client.bill_email_consent_recorded' || action === 'client.bill_email_consent_removed') {
    return action === 'client.bill_email_consent_removed'
      ? (<><strong className="font-medium">{actorEmail}</strong>{' removed the client’s consent to receive bills by email'}</>)
      : (<><strong className="font-medium">{actorEmail}</strong>{' recorded the client’s consent to receive bills by email at '}<em>{String(details.email ?? '')}</em></>)
  }
  if (action === 'user.job_title.changed') {
    return (<><strong className="font-medium">{actorEmail}</strong>{' changed a user’s job title to '}<em>{details.to ? String(details.to) : '(none)'}</em></>)
  }
  if (action === 'user.role.changed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` changed a user's role from `}
        <em>{String(details.from)}</em>
        {' to '}
        <em>{String(details.to)}</em>
      </>
    )
  }
  if (action === 'deadline_type.renamed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` renamed deadline type '`}
        <em>{String(details.from)}</em>
        {`' to '`}
        <em>{String(details.to)}</em>
        {`'`}
      </>
    )
  }
  if (action === 'deadline_type.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` deleted deadline type '`}
        <em>{String(details.name)}</em>
        {`'`}
      </>
    )
  }

  // ── Task actions (case-scoped) ──────────────────────────
  if (action === 'task.created') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` created task '`}
        <em>{String(details.title)}</em>
        {`'`}
      </>
    )
  }
  if (action === 'task.completed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` completed task '`}
        <em>{String(details.title)}</em>
        {`'`}
      </>
    )
  }
  if (action === 'task.assigned' && details.fromName && details.toName) {
    // Lead change (assignedToUserId) — names are resolved server-side.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` changed the lead on task '`}
        <em>{String(details.title)}</em>
        {`' from ${String(details.fromName)} to ${String(details.toName)}`}
      </>
    )
  }
  if (action === 'task.assigned') {
    // Intentionally MINIMAL — the payload's from/to are user IDs
    // (CUIDs) per the frontend Task model (assignedToUserId: string,
    // a Prisma user ref), not human-readable emails or names.
    // Showing raw IDs to users is hostile, so we drop them and keep
    // the sentence short. If Reuben later resolves these to emails
    // server-side, swap this branch for:
    //     `… reassigned task '{title}' from {from} to {to}`
    // matching the user.role.changed template.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` reassigned task '`}
        <em>{String(details.title)}</em>
        {`'`}
      </>
    )
  }
  if (action === 'task.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` deleted task '`}
        <em>{String(details.title)}</em>
        {`'`}
      </>
    )
  }

  // ── Case actions (case-scoped) ──────────────────────────
  if (action === 'case.created') {
    // B2: the claim type it was created with (absent on older rows).
    const type = claimTypeLabel(details.claimType)
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' created the case ('}
        <em>{String(details.matterNumber)}</em>
        {', '}
        <em>{String(details.clientSurname)}</em>
        {')'}
        {type && <>{' as '}<em>{type}</em></>}
      </>
    )
  }
  // B2 — the Claim card (Case control).
  if (action === 'case.claim_updated') {
    const changes = Array.isArray(details.changes) ? (details.changes as { field?: unknown; from?: unknown; to?: unknown }[]) : []
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' '}
        {changes.length === 0 ? 'updated the claim details' : changes.map((c, i) => (
          <React.Fragment key={i}>{i > 0 && (i === changes.length - 1 ? ' and ' : ', ')}{claimChangePhrase(c)}</React.Fragment>
        ))}
        {/* (B2b fix 2) the type change also removed the "no time limit" confirmation */}
        {details.abuseNoLimitRemoved === true && <>{', and removed the '}<em>no time limit</em>{' confirmation'}</>}
      </>
    )
  }
  if (action === 'case.limitation_override_set') {
    const reason = typeof details.reason === 'string' && details.reason ? <>{'. Reason: '}<em>{details.reason}</em></> : null
    // B2 follow-up: the own date became the case's Limitation deadline.
    if (details.deadline === 'created' || details.deadline === 'updated' || details.deadline === 'unchanged') {
      return (
        <>
          <strong className="font-medium">{actorEmail}</strong>
          {' set the Limitation deadline to '}
          <em>{formatReadableDate(details.date)}</em>
          {' (own date'}
          {details.deadline === 'updated' && details.previousDeadlineDate ? <>{', was '}<em>{formatReadableDate(details.previousDeadlineDate)}</em></> : null}
          {')'}
          {reason}
        </>
      )
    }
    // Only the reason changed (the deadline is left as it is).
    if (details.previousDate && details.previousDate === details.date) {
      return (
        <>
          <strong className="font-medium">{actorEmail}</strong>
          {' changed the reason for the own date ('}
          <em>{formatReadableDate(details.date)}</em>
          {')'}
          {reason}
        </>
      )
    }
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' set an own date: '}
        <em>{formatReadableDate(details.date)}</em>
        {details.previousDate ? <>{' (was '}<em>{formatReadableDate(details.previousDate)}</em>{')'}</> : null}
        {typeof details.reason === 'string' && details.reason ? <>{'. Reason: '}<em>{details.reason}</em></> : null}
      </>
    )
  }
  // B2b — child sexual abuse "no time limit" (s.11ZA).
  if (action === 'case.abuse_no_limit_confirmed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' confirmed '}<em>no time limit</em>{' (child sexual abuse, Limitation Act s.11ZA): sexual abuse, under 18 when it happened, never settled or decided by a court'}
      </>
    )
  }
  if (action === 'case.abuse_no_limit_unconfirmed') {
    return details.automatic === true ? (
      <>
        {'The '}<em>no time limit</em>{' confirmation was removed when '}
        <strong className="font-medium">{actorEmail}</strong>
        {' saved changes it no longer fits (claim type, type of abuse or dates)'}
      </>
    ) : (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' removed the '}<em>no time limit</em>{' confirmation (child sexual abuse); the usual limitation date applies again'}
      </>
    )
  }
  if (action === 'case.limitation_override_cleared') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' removed the own date ('}
        <em>{formatReadableDate(details.previousDate)}</em>
        {'); the estimate is used again'}
      </>
    )
  }
  if (action === 'case.closed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' closed the case on '}
        <em>{formatReadableDate(details.closeDate)}</em>
      </>
    )
  }
  if (action === 'case.reopened') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' reopened the case (was closed '}
        <em>{formatReadableDate(details.previousCloseDate)}</em>
        {')'}
      </>
    )
  }
  if (action === 'case.setup_finalised') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' finalised case setup ('}
        <em>{String(details.matterNumber)}</em>
        {', '}
        <em>{String(details.clientSurname)}</em>
        {')'}
      </>
    )
  }

  // ── Case-deadline actions (case-scoped) ─────────────────
  if (action === 'case_deadline.created') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' added a '}
        <em>{String(details.name)}</em>
        {' deadline for '}
        <em>{formatReadableDate(details.dueDate)}</em>
      </>
    )
  }
  if (action === 'case_deadline.completed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' marked the '}
        <em>{String(details.name)}</em>
        {' deadline (due '}
        <em>{formatReadableDate(details.dueDate)}</em>
        {') complete'}
      </>
    )
  }
  if (action === 'case_deadline.changed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' moved the '}
        <em>{String(details.name)}</em>
        {' deadline from '}
        <em>{formatReadableDate(details.from)}</em>
        {' to '}
        <em>{formatReadableDate(details.to)}</em>
      </>
    )
  }
  if (action === 'case_deadline.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' removed the '}
        <em>{String(details.name)}</em>
        {' deadline (was due '}
        <em>{formatReadableDate(details.dueDate)}</em>
        {')'}
      </>
    )
  }

  // ── More case actions (case-scoped) ─────────────────────
  if (action === 'case.assignee_changed') {
    // The old/new assignee are USER IDs (UUIDs) in details — NEVER
    // printed. Use the readable matterNumber + clientSurname instead.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' reassigned the case ('}
        <em>{String(details.matterNumber)}</em>
        {', '}
        <em>{String(details.clientSurname)}</em>
        {')'}
      </>
    )
  }
  if (action === 'case.client_synced') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated the client details on the case ('}
        <em>{String(details.matterNumber)}</em>
        {', '}
        <em>{String(details.clientSurname)}</em>
        {')'}
      </>
    )
  }
  if (action === 'case.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' deleted the case ('}
        <em>{String(details.matterNumber)}</em>
        {')'}
      </>
    )
  }
  if (action === 'witness.deleted') {
    // details holds only UUIDs (witnessId, caseId) — nothing readable,
    // so stay generic rather than print an id.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' removed a witness from a case'}
      </>
    )
  }

  // ── Activity actions (case-scoped) ──────────────────────
  if (action === 'activity.created') {
    // details carries clientId (UUID) + numbers only — no readable
    // client name or action type, so keep the sentence generic.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' logged an activity'}
      </>
    )
  }
  if (action === 'activity.updated') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated an activity'}
      </>
    )
  }
  if (action === 'activity.repriced') {
    // Admin re-price at the current grade rate (activityController.repriceActivities).
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' re-priced an activity at the current rate'}
      </>
    )
  }
  if (action === 'activity.deleted') {
    // details.action is the activity type (call / letter / memo) —
    // readable, so name it when present; else stay generic.
    const kind =
      typeof details.action === 'string' && details.action.trim().length > 0
        ? details.action.trim()
        : null
    if (kind) {
      return (
        <>
          <strong className="font-medium">{actorEmail}</strong>
          {' deleted a '}
          <em>{kind}</em>
          {' activity'}
        </>
      )
    }
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' deleted an activity'}
      </>
    )
  }

  // ── Client actions (firm-scoped) ────────────────────────
  if (action === 'client.created') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' added the client '}
        <em>{`${String(details.forename)} ${String(details.surname)}`.trim()}</em>
      </>
    )
  }
  if (action === 'client.updated') {
    // (Big-audit batch 4, M9) Which fields, with masked values (lib/auditMask).
    const changed = details.changed && typeof details.changed === 'object' ? details.changed as Record<string, { from?: unknown; to?: unknown }> : {}
    const fields = Object.keys(changed)
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated a client'}
        {fields.map((f, i) => <React.Fragment key={f}>{i === 0 ? ': ' : '; '}{clientFieldLabel(f)} <em>{clientChangeText(changed[f])}</em></React.Fragment>)}
      </>
    )
  }
  if (action === 'client.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' deleted the client '}
        <em>{`${String(details.forename)} ${String(details.surname)}`.trim()}</em>
      </>
    )
  }

  // ── File actions (case-scoped) ──────────────────────────
  // action is 'file.upload_url_created' (a file was uploaded), plus
  // downloaded / deleted. details carries the filename.
  if (action === 'file.upload_url_created') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' uploaded '}
        <em>{String(details.fileName)}</em>
      </>
    )
  }
  if (action === 'file.downloaded') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' downloaded '}
        <em>{String(details.fileName)}</em>
      </>
    )
  }
  if (action === 'file.deleted') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' deleted the file '}
        <em>{String(details.fileName)}</em>
      </>
    )
  }

  // ── TPI contact actions (firm-scoped) ───────────────────
  if (action === 'tpi_contact.created') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' added the TPI contact '}
        <em>{String(details.insurer)}</em>
      </>
    )
  }
  if (action === 'tpi_contact.updated') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated the TPI contact '}
        <em>{String(details.insurer)}</em>
      </>
    )
  }

  // ── More admin actions (firm-scoped, no caseId) ─────────
  if (action === 'user.grade.changed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {` changed a user's billing grade from `}
        <em>{String(details.from)}</em>
        {' to '}
        <em>{String(details.to)}</em>
      </>
    )
  }
  if (action === 'user.deactivated') {
    // targetId is the affected user's UUID — not printed; stay generic.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' deactivated a user account'}
      </>
    )
  }
  if (action === 'user.reactivated') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' reactivated a user account'}
      </>
    )
  }
  if (action === 'user.avatar_updated') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated their avatar photo'}
      </>
    )
  }
  if (action === 'user.avatar_removed') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' removed their avatar photo'}
      </>
    )
  }
  if (action === 'user.email_changed') {
    // details { from, to } — the member's old + new login email.
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' changed a login email from '}
        <em>{String(details.from ?? '—')}</em>
        {' to '}
        <em>{String(details.to ?? '—')}</em>
      </>
    )
  }
  if (action === 'user.2fa_reset') {
    // details.email is the affected user's email (readable). No secret or
    // codes are ever in the audit payload.
    const who =
      typeof details.email === 'string' && details.email.trim().length > 0
        ? details.email.trim()
        : null
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' reset two-factor authentication for '}
        <em>{who ?? 'a user'}</em>
      </>
    )
  }
  if (action === 'user.invited') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' invited '}
        <em>{String(details.email)}</em>
      </>
    )
  }
  if (action === 'user.joined') {
    // actorEmail here is the NEW user (the audit actor), so "joined".
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' joined the firm'}
      </>
    )
  }
  if (action === 'invite.resent') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' resent the invitation to '}
        <em>{String(details.email)}</em>
      </>
    )
  }
  if (action === 'invite.revoked') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' revoked the invitation to '}
        <em>{String(details.email)}</em>
      </>
    )
  }
  if (action === 'grade_rate.updated') {
    return (
      <>
        <strong className="font-medium">{actorEmail}</strong>
        {' updated the '}
        <em>{String(details.grade)}</em>
        {'-grade billing rate'}
      </>
    )
  }

  // ── Fallback (unknown action) ───────────────────────────
  // Safety net for FUTURE actions that don't yet have a dedicated
  // template above (all ~40 current actions do). Rather than leak the
  // raw dotted/snake_case action string and a targetType/UUID (what the
  // old fallback showed), we HUMANISE the action into a flowing
  // sentence in the same "{actor} {verb} {object}" voice as the
  // templates.
  //
  // Action convention is "domain.verb_phrase": dot-separated, verb
  // often snake_case, occasionally two dots (e.g. user.role.changed).
  // We split on the FIRST '.', then humanise each side by turning
  // underscores AND any remaining dots into spaces:
  //   "case.archived"           → domain "case",       verb "archived"
  //   "grade_rate.recalculated" → domain "grade rate", verb "recalculated"
  //   "user.role.changed"       → domain "user",       verb "role changed"
  //
  // Rendered as "{actor} {verb phrase} a/an {domain}" —
  //   "case.archived"           → "… archived a case"
  //   "grade_rate.recalculated" → "… recalculated a grade rate"
  //   "widget.frobnicated"      → "… frobnicated a widget"
  //
  // CHOICE: this "{verb} a {domain}" ordering (over a "{domain} {verb}"
  // log-line form) is deliberate — it matches the natural-sentence voice
  // of every template above and hits the common single-verb case
  // cleanly. Multi-word verb phrases (e.g. "status changed") read a
  // touch less smoothly but stay readable; crucially NO raw dotted /
  // snake_case tokens and NO targetId/UUID ever appear.
  const humanise = (s: string): string =>
    s.replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim()

  const actionStr = typeof action === 'string' ? action : ''
  const dotIdx = actionStr.indexOf('.')
  // domain = text before the FIRST dot (only when non-empty); verb =
  // everything after it (or the whole string when there's no dot).
  const domain = humanise(dotIdx > 0 ? actionStr.slice(0, dotIdx) : '')
  const verb = humanise(dotIdx >= 0 ? actionStr.slice(dotIdx + 1) : actionStr)

  let sentence: string
  if (verb && domain) {
    const article = /^[aeiou]/i.test(domain) ? 'an' : 'a'
    sentence = `${verb} ${article} ${domain}`
  } else if (verb) {
    // No dot / no domain — humanised verb alone still beats raw tokens.
    sentence = verb
  } else if (domain) {
    // Trailing-dot oddity ("case.") — verb missing; stay generic.
    sentence = `updated a ${domain}`
  } else {
    // Empty / unparseable action — last-resort generic, never a token.
    sentence = 'performed an action'
  }

  return (
    <>
      <strong className="font-medium">{actorEmail}</strong>
      {` ${sentence}`}
    </>
  )
}
